/**
 * What can be said about a stored SystemConfig row as a whole, for every
 * category: whether it can be served at all, and whether it carries an on/off
 * setting the running system reads.
 */

import { z } from "zod";

import type { ConfigCategory } from "../zsystem-config";

/**
 * A stored row the list could not serve in `SystemConfigSchema`'s shape at all —
 * a category the code no longer knows, a `config` that is not a JSON object, a
 * key or version out of range. It is listed by what can still be said about it,
 * instead of failing the list for every other row.
 */
export const UnreadableSystemConfigRowSchema = z.object({
  id: z.string(),
  category: z.string(),
  key: z.string(),
  parseError: z.string()
});

export type UnreadableSystemConfigRow = z.infer<typeof UnreadableSystemConfigRowSchema>;

/**
 * The categories whose rows carry an on/off setting the running system reads.
 *
 * Only `cron`: its setting is `config.enabled`, and the write path keeps the
 * `enabled` column as its second spelling. For every other category no
 * production code reads the column — the backend's
 * `enabled-column-readers.spec.ts` holds that as a type-resolved census and
 * names any new reader — so the admin API refuses the field for them
 * (`CONFIG_ENABLED_NOT_READ`) and the editor does not offer it. A category that
 * starts reading the column is added here, in the same change as its reader.
 */
export const CONFIG_CATEGORIES_WITH_ENABLED_SETTING = [
  "cron"
] as const satisfies readonly ConfigCategory[];

export function configCategoryHasEnabledSetting(category: ConfigCategory): boolean {
  const withSetting: readonly ConfigCategory[] = CONFIG_CATEGORIES_WITH_ENABLED_SETTING;
  return withSetting.includes(category);
}

/**
 * Who writes a row's `description` COLUMN, per category.
 *
 *   - `admin` — the admin's own note. Nothing in the code writes it: neither the
 *     boot sync seeding a missing key nor reset restoring one, so the column holds
 *     only what an admin typed. The code's own description reaches the admin
 *     panel beside the row, as `SystemConfigSchema.codeDefault`, and the panel
 *     shows the column only as a note — so a copy of the code's prose in the
 *     column would be a second, freezing copy shown as someone's annotation.
 *   - `code-seed` — the code default's description, written when the row is
 *     seeded or reset. Right only for a category whose code description the panel
 *     is NOT served any other way. Every category is served `codeDefault`, so no
 *     category is one.
 *
 * `satisfies Record<ConfigCategory, …>` makes it exhaustive: a category added
 * without an owner is a compile error. The backend's
 * `no-code-default-writes-an-admin-owned-description.spec.ts` holds every
 * category, and every key with a code default, to what this says.
 */
export type ConfigDescriptionColumnOwner = "admin" | "code-seed";

export const CONFIG_DESCRIPTION_COLUMN_OWNERSHIP = {
  cron: "admin",
  rate_limit: "admin",
  cache: "admin",
  circuit_breaker: "admin",
  retry_policy: "admin",
  echo: "admin",
  deletion: "admin",
  conversation: "admin",
  vibe: "admin",
  public_api: "admin",
  twilio: "admin",
  agent_auth: "admin",
  sandbox: "admin",
  deployments: "admin",
  "agent-tools": "admin",
  identity: "admin"
} as const satisfies Record<ConfigCategory, ConfigDescriptionColumnOwner>;

/** The refusals about a stored row as a whole, shared by every category. */
export const SYSTEM_CONFIG_ROW_ERROR_CODES = [
  "CONFIG_ENABLED_NOT_READ",
  "CONFIG_ROW_UNREADABLE"
] as const;

export type SystemConfigRowErrorCode = (typeof SYSTEM_CONFIG_ROW_ERROR_CODES)[number];
